import React, { useState, useEffect } from 'react';
import { Bug, RefreshCw, Trash2, Check, Clock, XCircle, AlertTriangle } from 'lucide-react';

const AdminBugReports = ({ API_URL }) => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', severity: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedReport, setExpandedReport] = useState(null);
  const [adminNotes, setAdminNotes] = useState({});

  const loadReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      if (filters.status) params.append('status', filters.status);
      if (filters.severity) params.append('severity', filters.severity);

      const response = await fetch(`${API_URL}/api/bug-reports?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setReports(data.reports || []);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error('Error loading bug reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bug-reports/stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error loading bug report stats:', err);
    }
  };

  useEffect(() => {
    loadReports();
    loadStats();
  }, [page, filters]);

  const updateReportStatus = async (reportId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/bug-reports/${reportId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ status: newStatus, admin_notes: adminNotes[reportId] || '' })
      });
      loadReports();
      loadStats();
    } catch (err) {
      console.error('Error updating report:', err);
    }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm('Delete this bug report?')) return;
    try {
      await fetch(`${API_URL}/api/bug-reports/${reportId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      loadReports();
      loadStats();
    } catch (err) {
      console.error('Error deleting report:', err);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <Clock size={14} />;
      case 'in_progress': return <RefreshCw size={14} />;
      case 'resolved': return <Check size={14} />;
      case 'closed': return <XCircle size={14} />;
      default: return <Clock size={14} />;
    }
  };

  return (
    <div className="bug-reports-section">
      <h3><Bug size={20} style={{ marginRight: '8px' }} />Bug Reports</h3>
      
      {/* Stats Overview */}
      {stats && (
        <div className="bug-stats-row">
          <div className="bug-stat">
            <span className="bug-stat-value">{stats.total}</span>
            <span className="bug-stat-label">Total</span>
          </div>
          <div className="bug-stat open">
            <span className="bug-stat-value">{stats.by_status?.open || 0}</span>
            <span className="bug-stat-label">Open</span>
          </div>
          <div className="bug-stat in-progress">
            <span className="bug-stat-value">{stats.by_status?.in_progress || 0}</span>
            <span className="bug-stat-label">In Progress</span>
          </div>
          <div className="bug-stat resolved">
            <span className="bug-stat-value">{stats.by_status?.resolved || 0}</span>
            <span className="bug-stat-label">Resolved</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bug-reports-filters">
        <select 
          value={filters.status} 
          onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
        >
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select 
          value={filters.severity} 
          onChange={(e) => { setFilters({ ...filters, severity: e.target.value }); setPage(1); }}
        >
          <option value="">All Severity</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
        <button className="btn-sm btn-secondary" onClick={() => { loadReports(); loadStats(); }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="loading-state">Loading bug reports...</div>
      ) : reports.length === 0 ? (
        <div className="empty-state small">
          <Bug size={32} />
          <p>No bug reports found</p>
        </div>
      ) : (
        <div className="bug-reports-list">
          {reports.map(report => (
            <div key={report.report_id} className="bug-report-card">
              <div className="bug-report-header">
                <div>
                  <div className="bug-report-title">{report.title}</div>
                  <div className="bug-report-meta">
                    {report.user_name} ({report.user_email}) • {report.user_plan} plan • {new Date(report.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="bug-report-badges">
                  <span className={`severity-badge ${report.severity}`}>{report.severity}</span>
                  <span className={`bug-status-badge ${report.status}`}>
                    {getStatusIcon(report.status)} {report.status.replace('_', ' ')}
                  </span>
                </div>
              </div>

              <div className="bug-report-description">{report.description}</div>

              <div className="bug-report-info">
                <span>Page: {report.current_page}</span>
                <span>Browser: {report.browser_info}</span>
              </div>

              {expandedReport === report.report_id && (
                <div className="bug-report-expanded">
                  <textarea
                    className="admin-notes-input"
                    placeholder="Add admin notes..."
                    value={adminNotes[report.report_id] || report.admin_notes || ''}
                    onChange={(e) => setAdminNotes({ ...adminNotes, [report.report_id]: e.target.value })}
                  />
                </div>
              )}

              <div className="bug-report-actions">
                <button 
                  className="btn-sm btn-secondary"
                  onClick={() => setExpandedReport(expandedReport === report.report_id ? null : report.report_id)}
                >
                  {expandedReport === report.report_id ? 'Collapse' : 'Expand'}
                </button>
                <select
                  className="status-select"
                  value={report.status}
                  onChange={(e) => updateReportStatus(report.report_id, e.target.value)}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
                <button 
                  className="btn-sm btn-danger"
                  onClick={() => deleteReport(report.report_id)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="admin-pagination">
          <button 
            className="btn-sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button 
            className="btn-sm"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminBugReports;
